import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { ExperienceModal } from "./ExperienceModal";
import { useDeleteUserProfileSectionItem } from "Constant/apiHooks";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  VIEW_PROFILE_SECTIONS,
} from "Constant/constants";
import { formatMonthYear } from "Utils";

export const Experience = ({ experienceList, fetchData, isCurrentUserProfile }) => {
  const { t } = useTranslation();

  const { deleteUserProfileSectionItem } = useDeleteUserProfileSectionItem();

  const [experienceModal, setShowExperienceModal] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);

  const getExperienceType = (type) => {
    const labelKey = EMPLOYMENT_TYPE_OPTIONS.find(
      (e) => e.value === type
    )?.label;
    return labelKey ? t(labelKey) : "";
  };

  const toggleExperienceModal = (exp) => {
    setShowExperienceModal((prev) => !prev);
    if (exp) {
      setSelectedExperience(exp);
    } else {
      setSelectedExperience(null);
    }
  };

  const handleDeleteExperience = async (gid) => {
    try {
      const payload = {
        key: VIEW_PROFILE_SECTIONS.experience,
        [VIEW_PROFILE_SECTIONS.experience]: {
          gid,
        },
      };
      await deleteUserProfileSectionItem(payload);
      fetchData();
    } catch {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  return (
    <>
      <div className="p-4 rounded-lg border border-grey-200 bg-white d-flex flex-column gap-0">
        <div className="d-flex flex-wrap justify-content-between align-items-center column-gap-3 row-gap-2 mb-3">
          <div>
            <h4 className="mb-0 text-md font-semibold text-grey-800">
              {t("auth.viewProfile.experienceAndHighlights")}
            </h4>
            {experienceList.length === 0 && (
              <span className="text-grey-400">
                {t("auth.viewProfile.noExperience.info")}
              </span>
            )}
          </div>
          {isCurrentUserProfile && (
            <div className="d-flex align-items-center gap-1">
              <Button
                className="btn btn-gray-light font-medium rounded-lg d-inline-flex align-items-center gap-1"
                onClick={() => toggleExperienceModal()}
              >
                <i className="bx bx-plus"></i>{" "}
                {t("auth.viewProfile.addExperience")}
              </Button>
            </div>
          )}
        </div>
        <ul className="exp-highlight-list list-unstyled mb-0 d-flex flex-column justify-content-center row-gap-1">
          {experienceList && experienceList.length > 0 ? (
            experienceList.map((exp, idx) => (
              <li key={idx.toString()}>
                <div className="d-flex gap-3 py-4">
                  <div className="w-16 h-16 flex-auto rounded-circle overflow-hidden d-flex justify-content-center align-items-center bg-grey-100">
                    <img
                      src="/assets/images/placeholder.jpg"
                      alt=""
                      className="w-100 h-100 object-fit-cover"
                    />
                  </div>
                  <div className="d-flex flex-column gap-1 w-100">
                    <div className="d-flex gap-3">
                      <h5 className="mb-0 text-base font-semibold text-grey-800">
                        {exp.title}{" "}
                        <small className="px-2 py-1 text-xs rounded-md badge badge-green-500">
                          {getExperienceType(exp.employmentType)}
                        </small>
                      </h5>
                      {isCurrentUserProfile && (
                        <div className="ms-auto d-flex align-items-center gap-3">
                          <Button
                            variant="link"
                            className="p-0 text-base text-grey-600 text-decoration-none"
                            onClick={() => toggleExperienceModal(exp)}
                          >
                            <i className="bx bx-pencil"></i>
                          </Button>
                          <Button
                            variant="link"
                            className="p-0 text-base text-grey-600 text-decoration-none"
                            onClick={() => handleDeleteExperience(exp.gid)}
                          >
                            <i className="bx bx-trash"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                    <span>
                      {exp.company} {exp.location ? `, ${exp.location}` : ""}
                    </span>
                    <span>
                      {exp.startDate && formatMonthYear(exp.startDate)}
                      {" - "}{" "}
                      {exp.currentlyWorking
                        ? t("common.label.present")
                        : exp.endDate && formatMonthYear(exp.endDate)}
                      (<strong>4 months</strong>)
                    </span>
                    <span>{exp.responsibility}</span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li>
              <div className="d-flex gap-3 py-4">
                <div className="w-16 h-16 flex-auto rounded-circle overflow-hidden d-flex justify-content-center align-items-center bg-grey-100">
                  <img
                    src="/assets/images/placeholder.jpg"
                    alt=""
                    className="w-100 h-100 object-fit-cover"
                  />
                </div>
                <div className="d-flex flex-column gap-1 w-100">
                  <h5 className="mb-1 text-base font-semibold text-grey-400">
                    {t("auth.viewProfile.noExperience.role")}
                  </h5>
                  <span className="text-grey-400">
                    {t("auth.viewProfile.noExperience.company")}
                  </span>
                  <span className="text-grey-400">
                    {t("auth.viewProfile.noExperience.startAndEndDate")}
                  </span>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>

      {experienceModal && (
        <ExperienceModal
          show={experienceModal}
          experienceData={selectedExperience}
          toggle={toggleExperienceModal}
          fetchData={fetchData}
        />
      )}
    </>
  );
};
